import { linkedSignal, Resource, ResourceStatus, Signal } from '@angular/core';
import { ReadonlyRepository } from '../../repositories/abstraction';
import { cancelableResource } from '../../signals/cancelable-resource';

export interface RepositoryObjectServiceOptions<T> {
  defaultValue?: T;
  skipInitialLoad?: boolean;
}

export abstract class ReadonlyRepositoryObjectService<T, TResult, TParams> implements Resource<T | undefined>{

  abstract params: Signal<TParams>;

  protected _resource = cancelableResource({
    skipInitialLoad: this.options?.skipInitialLoad ?? false,
    request: () => ({
      params: this.params(),
    }),
    loader: async (param) => {
      return await this.repository.load(param.request.params);
    }
  });

  resource = this._resource.asReadonly();
  status = this._resource.status;
  isLoading = this._resource.isLoading;
  error = this._resource.error;
  hasValue(): this is Resource<Exclude<T, undefined>> {
    return this.resource.hasValue();
  };

  value = linkedSignal<T | undefined>(() => this.getValue(this._resource.value()));

  constructor(protected repository: ReadonlyRepository<TResult, TParams>, private options: RepositoryObjectServiceOptions<T> = {}) {
    if(options.defaultValue)
      this.value.set(options.defaultValue);
  }

  load(): boolean {
    if(this._resource.status() === ResourceStatus.Idle)
      return this._resource.reload();
    return false;
  }

  cancel(): void {
    this._resource.cancel()
  }

  reload(): boolean {
    return this._resource.reload();
  }

  protected abstract getValue(result: TResult): T;
  protected abstract getValue(result: TResult | undefined): T | undefined;
}
